/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { GoogleChartsLoader } from '@gh/core-external';
import { filter$ } from '@gh/rx/operators';
import { omit } from 'lodash';
import { ChartSpecsWithLegend } from '../../charts.model';

@Component({
  standalone: false,
  selector: 'gh-chart',
  templateUrl: 'chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent implements OnChanges {
  @Input() data: ChartSpecsWithLegend;

  @Output() ready = new EventEmitter<boolean>();
  @Output() error = new EventEmitter<google.visualization.ErrorEventObject>();

  drawn: boolean = false;
  private wrapper: google.visualization.ChartWrapper;

  constructor(
    private chartEl: ElementRef,
    private loader: GoogleChartsLoader,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    setTimeout(() => this.build(), 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const key = 'data';

    if (changes[key]) {
      this.build();
    }
  }

  private build(): void {
    this.loader
      .load('corechart', 'table').pipe(
      filter$(Boolean))
      .subscribe(() => {
        this
          .draw()
          .registerEvents();
      });
  }

  private draw(): ChartComponent {
    let { data } = this;

    if (data.legendLayout) {
      data = <google.visualization.ChartSpecs>omit(
        { ...data, options: { ...data.options, legend: { position: 'none' } } },
        'legendLayout',
      );
    }

    if (data.axisLayout) {
      data = <google.visualization.ChartSpecs>omit(
        { ...data, options: { ...data.options, hAxis: { textPosition: 'none' } } },
        'axisLayout',
      );
    }

    this.wrapper = new google.visualization.ChartWrapper(data);
    this.wrapper.draw(this.chartEl.nativeElement.querySelector('#chart'));

    this.drawn = true;
    this.changeDetectorRef.markForCheck();

    return this;
  }

  private registerEvents(): ChartComponent {
    google.visualization.events
      .addListener(
        this.wrapper,
        'ready',
        () => this.ready.emit(true),
      );

    google.visualization.events
      .addListener(
        this.wrapper,
        'error',
        (error: google.visualization.ErrorEventObject) => this.error.emit(error),
      );

    return this;
  }
}
//Remove later